import { useState } from "react";
import { Box, Button, Container, IconButton, Skeleton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { FestivalPageHelmet } from "@/app/(public)/_seo/FestivalPageHelmet";
import { PublicFestival, PublicFestivalShow } from "@/app/_services/PublicFestivalService";
import { HELD_LABEL, showAvailability } from "@/app/_utils/availability";
import { dayjsShow } from "@/app/_utils/dayjs";
import { DEFAULT_OG_IMAGE_PATH } from "@/app/_utils/constants";
import { PUBLIC_PAGE_CONTAINER_MAX_WIDTH, publicPageContainerSx } from "@/app/_utils/publicLayout";
import { ShopDrawing, characterFor } from "@/app/(public)/festivals/FoodTourArt";

const pageFont =
  '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const pageBg = "#0a0a0a";
const accent = "#FF5147";
const textPrimary = "#ffffff";
const textMuted = "#999999";
const hairline = "rgba(255,255,255,0.12)";
const cardBorder = "rgba(255,255,255,0.10)";
const cardBg = "#141414";
const radius = "16px";

const reducedMotionSx = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none !important",
    animation: "none !important",
  },
} as const;

type Availability = "sold-out" | "held" | "few-left" | "available";

export type TourCartLine = {
  id: string;
  quantity: number;
  total_price: number;
  showId: string;
};

function clockLabel(value: ReturnType<typeof dayjsShow>): string {
  return value.minute() === 0 ? value.format("h A") : value.format("h:mm A");
}

function groupShows(shows: PublicFestivalShow[]): PublicFestivalShow[][] {
  const groups = new Map<string, PublicFestivalShow[]>();
  for (const show of shows) {
    const venue = show.location?.name || show.title;
    const key = `${dayjsShow(show.start).format("YYYY-MM-DD")}|${venue}`;
    const list = groups.get(key) ?? [];
    list.push(show);
    groups.set(key, list);
  }
  return Array.from(groups.values())
    .map((list) => list.sort((a, b) => dayjsShow(a.start).valueOf() - dayjsShow(b.start).valueOf()))
    .sort((a, b) => dayjsShow(a[0].start).valueOf() - dayjsShow(b[0].start).valueOf());
}

function sittingName(show: PublicFestivalShow, group: PublicFestivalShow[]): string {
  if (group.length < 2) return "";
  return show.id === group[0].id ? "Early" : "Late";
}

function doorLabel(show: PublicFestivalShow): string {
  return `Doors at ${clockLabel(dayjsShow(show.start).subtract(30, "minute"))}`;
}

function placeNote(name: string): string {
  const value = name.toLowerCase();
  if (value.includes("greengrass") || value.includes("barney")) return "Upper West Side deli. Room for a few dozen.";
  if (value.includes("veselka")) return "East Village Ukrainian, after hours.";
  if (value.includes("russ")) return "The appetizing counter, turned into a comedy room.";
  if (value.includes("zabar")) return "A neighborhood landmark New York already treats like theater.";
  if (value.includes("economy") || value.includes("candy")) return "Sweet, packed, and pure Lower East Side.";
  return "";
}

function availabilityFor(show: PublicFestivalShow): Availability {
  const state = showAvailability(show);
  if (state === "held") return "held";
  if (state === "sold_out") return "sold-out";
  if (show.capacity > 0 && show.inventory_quantity != null) {
    const sold = show.capacity - show.inventory_quantity - (show.held_quantity ?? 0);
    if (sold / show.capacity >= 0.85) return "few-left";
  }
  return "available";
}

function statusFor(show: PublicFestivalShow): { label: string; color: string } {
  const availability = availabilityFor(show);
  if (availability === "sold-out") return { label: "Sold out", color: accent };
  if (availability === "held") return { label: HELD_LABEL, color: "#fcd34d" };
  const left = show.inventory_quantity;
  if (availability === "few-left" && left != null && left > 0) return { label: `${left} left`, color: accent };
  if (availability === "few-left") return { label: "Few left", color: accent };
  return { label: "Available", color: "#5dcaa5" };
}

function formatDateRange(shows: PublicFestivalShow[]): string {
  if (shows.length === 0) return "";
  const sorted = [...shows].sort((a, b) => dayjsShow(a.start).valueOf() - dayjsShow(b.start).valueOf());
  const first = dayjsShow(sorted[0].start);
  const last = dayjsShow(sorted[sorted.length - 1].start);
  if (first.isSame(last, "day")) return first.format("MMM D, YYYY");
  if (first.isSame(last, "month")) return `${first.format("MMM D")}–${last.format("D, YYYY")}`;
  if (first.isSame(last, "year")) return `${first.format("MMM D")} – ${last.format("MMM D, YYYY")}`;
  return `${first.format("MMM D, YYYY")} – ${last.format("MMM D, YYYY")}`;
}

const controlSx = {
  width: 128,
  height: 32,
  fontSize: 13,
  borderRadius: 999,
  textTransform: "none" as const,
  fontWeight: 700,
  fontFamily: pageFont,
  fontSize: 14,
  boxShadow: "none",
  "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
};

function QuantityStepper({
  quantity,
  pending,
  max,
  label,
  onChange,
}: {
  quantity: number;
  pending: boolean;
  max: number;
  label: string;
  onChange: (delta: number) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 36,
        border: `1px solid ${hairline}`,
        borderRadius: 999,
        px: 0.25,
      }}
    >
      <IconButton
        size="small"
        disabled={pending || quantity <= 0}
        aria-label={`Remove one ticket for ${label}`}
        onClick={() => onChange(-1)}
        sx={{ color: textPrimary, "&:focus-visible": { outline: "2px solid #fff" } }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Box sx={{ minWidth: 18, textAlign: "center", fontWeight: 700, fontSize: 14 }}>
        {pending ? <Skeleton variant="text" width={12} sx={{ bgcolor: "rgba(255,255,255,0.15)", mx: "auto" }} /> : quantity}
      </Box>
      <IconButton
        size="small"
        disabled={pending || quantity >= max}
        aria-label={`Add one ticket for ${label}`}
        onClick={() => onChange(1)}
        sx={{ color: textPrimary, "&:focus-visible": { outline: "2px solid #fff" } }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function ShowCard({
  shows,
  quantityFor,
  pendingShowId,
  onChange,
}: {
  shows: PublicFestivalShow[];
  quantityFor: (show: PublicFestivalShow) => number;
  pendingShowId: string | null;
  onChange: (show: PublicFestivalShow, delta: number) => void;
}) {
  const [hovering, setHovering] = useState(false);
  const [hoveringId, setHoveringId] = useState<string | null>(null);
  const show = shows[0];
  const when = dayjsShow(show.start);
  const cast = characterFor(`${show.location?.name ?? ""} ${show.title}`);
  const venue = show.location?.name || show.title;
  const note = placeNote(venue);
  const picked = shows.some((item) => quantityFor(item) > 0);
  const single = shows.length < 2;

  return (
    <Box
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 1,
        bgcolor: picked ? "#1a1212" : cardBg,
        border: `1px solid ${picked ? accent : cardBorder}`,
        borderRadius: radius,
        transition: "border-color 180ms ease, background-color 180ms ease",
        ...reducedMotionSx,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Box sx={{ fontSize: 10, letterSpacing: 0.6, color: textMuted, fontWeight: 700 }}>{when.format("ddd").toUpperCase()}</Box>
          <Box sx={{ fontSize: 26, fontWeight: 800, lineHeight: 0.9 }}>{when.format("D")}</Box>
          <Box sx={{ fontSize: 10, letterSpacing: 0.6, color: textMuted, fontWeight: 700 }}>{when.format("MMM").toUpperCase()}</Box>
        </Box>
        <Box sx={{ width: 56, height: 72, flex: "none", overflow: "visible" }}>
          {cast && <ShopDrawing id={cast} alt="" fill active={hovering || picked} />}
        </Box>
      </Box>
      <Box sx={{ mt: 1, aspectRatio: { xs: "16 / 9", md: "4 / 3" }, borderRadius: "10px", overflow: "hidden", bgcolor: "#000" }}>
        <Box
          component="img"
          src={show.img_url || DEFAULT_OG_IMAGE_PATH}
          alt={venue}
          sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 18%" }}
        />
      </Box>
      <Box sx={{ mt: 1, fontWeight: 800, fontSize: 15, lineHeight: 1.15 }}>{venue}</Box>
      {note && <Box sx={{ color: "#ccc", fontSize: 12, lineHeight: 1.35, mt: single ? 1 : 0.35 }}>{note}</Box>}
      <Box sx={{ mt: single ? 1.75 : 1.25, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {shows.map((item, index) => {
          const status = statusFor(item);
          const blocked = availabilityFor(item) === "sold-out" || availabilityFor(item) === "held";
          const quantity = quantityFor(item);
          const added = quantity > 0;
          const pending = pendingShowId === item.id;
          const sitting = sittingName(item, shows);
          const name = sitting ? `${venue}, ${sitting}` : venue;
          return (
            <Box
              key={item.id}
              sx={{
                pt: index > 0 ? 1.5 : 0,
                borderTop: index > 0 ? `1px solid ${hairline}` : "none",
              }}
            >
              {sitting && (
                <Box sx={{ color: accent, fontWeight: 800, fontSize: 12, letterSpacing: 0.4 }}>
                  {sitting.toUpperCase()}
                </Box>
              )}
              <Box sx={{ color: "#ccc", fontSize: 13, mt: sitting ? 0.35 : 0, lineHeight: 1.35 }}>
                {clockLabel(dayjsShow(item.start))}. {doorLabel(item)}.
              </Box>
              <Box sx={{ mt: single ? 1.25 : 0.5, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1 }}>${Math.round(item.ticket_price)}</Box>
                <Box sx={{ color: status.color, fontSize: 12, fontWeight: 700 }}>{status.label}</Box>
              </Box>
              <Box sx={{ mt: single ? 1.25 : 0.75 }}>
                {blocked ? (
                  <Button fullWidth disabled variant="contained" disableElevation sx={{ ...controlSx, width: "100%", bgcolor: "rgba(255,255,255,0.08) !important", color: "rgba(255,255,255,0.55) !important" }}>
                    {availabilityFor(item) === "held" ? HELD_LABEL : "Sold out"}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    disabled={pending}
                    aria-pressed={added}
                    aria-label={added ? `Remove ${name}` : `Add ${name}`}
                    onMouseEnter={() => setHoveringId(item.id)}
                    onMouseLeave={() => setHoveringId(null)}
                    onClick={added ? () => onChange(item, -quantity) : () => onChange(item, 1)}
                    sx={{
                      ...controlSx,
                      width: "100%",
                      minWidth: 0,
                      px: { xs: 1, md: 2 },
                      fontSize: { xs: 13, md: 14 },
                      bgcolor: added ? "transparent" : accent,
                      color: "#fff",
                      border: added ? `1px solid ${accent}` : "1px solid transparent",
                      "&:hover": { bgcolor: added ? "rgba(255,81,71,0.12)" : "#ff675e" },
                    }}
                  >
                    {added ? (hoveringId === item.id ? "Remove" : "Added") : "+ Add show"}
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function SummaryLines({
  shows,
  lines,
  pendingShowId,
  quantityFor,
  maxFor,
  onChange,
  compact = false,
}: {
  shows: PublicFestivalShow[];
  lines: TourCartLine[];
  pendingShowId: string | null;
  quantityFor: (show: PublicFestivalShow) => number;
  maxFor: (show: PublicFestivalShow) => number;
  onChange: (show: PublicFestivalShow, delta: number) => void;
  compact?: boolean;
}) {
  if (lines.length === 0) {
    return <Box sx={{ color: textMuted, fontSize: 14, py: 1 }}>Nothing selected yet. Add a night and it shows up here.</Box>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {lines.map((line) => {
        const show = shows.find((item) => item.id === line.showId);
        if (!show) return null;
        const when = dayjsShow(show.start);
        const group = groupShows(shows).find((items) => items.some((item) => item.id === show.id)) ?? [show];
        const sitting = sittingName(show, group);
        const cast = characterFor(`${show.location?.name ?? ""} ${show.title}`);
        const quantity = quantityFor(show);
        const lineTitle = sitting ? `${show.title}, ${sitting}` : show.title;
        if (compact) {
          return (
            <Box key={line.id} sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 36 }}>
              <Box sx={{ width: 32, height: 40, flex: "none", overflow: "visible" }}>{cast && <ShopDrawing id={cast} alt="" fill />}</Box>
              <Box sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lineTitle}</Box>
              <QuantityStepper quantity={quantity} pending={pendingShowId === show.id} max={maxFor(show)} label={show.title} onChange={(delta) => onChange(show, delta)} />
              <Box sx={{ width: 36, textAlign: "right", fontWeight: 800, fontSize: 14 }}>${Math.round(line.total_price ?? 0)}</Box>
            </Box>
          );
        }
        return (
          <Box
            key={line.id}
            sx={{
              display: "grid",
              gridTemplateColumns: compact ? "32px minmax(0, 1fr) auto" : "44px 56px minmax(0, 1fr)",
              gap: 1,
              alignItems: "center",
              animation: "ft-line 220ms ease",
              "@keyframes ft-line": {
                from: { opacity: 0, transform: "translateY(6px)" },
                to: { opacity: 1, transform: "none" },
              },
              ...reducedMotionSx,
            }}
          >
            <Box sx={{ width: compact ? 32 : 40, height: compact ? 40 : 52, overflow: "visible" }}>
              {cast && <ShopDrawing id={cast} alt="" fill />}
            </Box>
            {!compact && (
              <Box sx={{ width: 56, height: 40, borderRadius: "8px", overflow: "hidden", bgcolor: "#000" }}>
                <Box component="img" src={show.img_url || DEFAULT_OG_IMAGE_PATH} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lineTitle}</Box>
              <Box sx={{ color: textMuted, fontSize: 12, mt: 0.25, whiteSpace: "nowrap" }}>
                {when.format("ddd D MMM")} · {clockLabel(when)} · {doorLabel(show)}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: compact ? 0.5 : 0.75 }}>
                <QuantityStepper
                  quantity={quantity}
                  pending={pendingShowId === show.id}
                  max={maxFor(show)}
                  label={show.title}
                  onChange={(delta) => onChange(show, delta)}
                />
                <Box sx={{ fontWeight: 800, fontSize: 14 }}>${Math.round(line.total_price ?? 0)}</Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function FoodTourBooking({
  festival,
  shows,
  lines,
  passTotal,
  pendingShowId,
  samplePreview,
  checkoutDisabled,
  quantityFor,
  maxFor,
  onChange,
}: {
  festival: PublicFestival;
  shows: PublicFestivalShow[];
  lines: TourCartLine[];
  passTotal: number;
  pendingShowId: string | null;
  samplePreview: boolean;
  checkoutDisabled: boolean;
  quantityFor: (show: PublicFestivalShow) => number;
  maxFor: (show: PublicFestivalShow) => number;
  onChange: (show: PublicFestivalShow, delta: number) => void;
}) {
  const nights = new Set(shows.map((show) => dayjsShow(show.start).format("YYYY-MM-DD"))).size;
  const venues = new Set(shows.map((show) => show.location?.name || show.title)).size;
  const venueGroups = groupShows(shows);
  const dateRange = formatDateRange(shows);
  const city = shows.find((show) => show.location?.city)?.location?.city ?? "";
  const eyebrow = [dateRange, city].filter(Boolean).join(" · ").toUpperCase();
  const continueSx = {
    borderRadius: 999,
    textTransform: "none" as const,
    fontWeight: 700,
    fontFamily: pageFont,
    py: 1.15,
    bgcolor: checkoutDisabled ? "rgba(255,255,255,0.12) !important" : accent,
    color: checkoutDisabled ? "rgba(255,255,255,0.45) !important" : "#fff",
    "&:hover": { bgcolor: checkoutDisabled ? "rgba(255,255,255,0.12)" : "#ff675e" },
    "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
  };

  const checkout = (
    <Button
      fullWidth
      disabled={checkoutDisabled}
      href={samplePreview || checkoutDisabled ? undefined : "/cart"}
      onClick={samplePreview ? (event) => event.preventDefault() : undefined}
      variant="contained"
      disableElevation
      sx={continueSx}
    >
      {lines.length === 0 ? "Select shows to continue" : "Continue to checkout"}
    </Button>
  );

  return (
    <>
      <FestivalPageHelmet
        slug={festival.slug}
        title="Underground Overground Local Legends Food Tour"
        description="Underground Overground Local Legends Food Tour"
        imgUrl="https://comedyuo.com/images/food-tour/hannah-walk.png"
        exactTitle
        noIndex={!festival.published}
      />
      <Header />
      {samplePreview && (
        <Box sx={{ position: "sticky", top: 0, zIndex: 40, bgcolor: accent, color: "#000", textAlign: "center", fontWeight: 700, fontSize: 14, fontFamily: pageFont, py: 1, px: 2 }}>
          Sample layout. These shows are not for sale.
        </Box>
      )}
      <Box sx={{ bgcolor: pageBg, color: textPrimary, fontFamily: pageFont, minHeight: "100vh", pb: { xs: 16, md: 14 } }}>
        <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={{ ...publicPageContainerSx, pt: { xs: 3, md: 4 }, pb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1.05fr)" }, gap: { xs: 2.5, md: 4 }, alignItems: "center" }}>
            <Box>
              <Box sx={{ color: accent, fontWeight: 800, letterSpacing: 1.4, fontSize: 12 }}>FOOD TOUR</Box>
              {eyebrow && <Box sx={{ color: textMuted, fontWeight: 800, letterSpacing: 1.2, fontSize: 12, mt: 1 }}>{eyebrow}</Box>}
              <Box component="h1" sx={{ m: 0, mt: 1, fontWeight: 800, fontSize: { xs: 36, md: 52 }, lineHeight: 0.95, letterSpacing: -1.1 }}>
                Welcome to the <Box component="span" sx={{ whiteSpace: "nowrap" }}>Food Tour.</Box>
              </Box>
              <Box sx={{ mt: 1, fontWeight: 800, fontSize: { xs: 22, md: 28 }, lineHeight: 1.1 }}>
                {nights} night{nights === 1 ? "" : "s"},{" "}
                <Box component="span" sx={{ color: accent }}>
                  {venues} Neighborhood Classic{venues === 1 ? "" : "s"}
                </Box>
              </Box>
              <Box sx={{ mt: 1.5, color: "#ccc", fontSize: 16, lineHeight: 1.5, maxWidth: 520 }}>
                Come join us as we produce shows inside <Box component="span" sx={{ color: accent, fontWeight: 800 }}>these local legends</Box>.
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", borderBottom: "1px solid rgba(255,255,255,0.16)", pb: 0.5 }}>
              <Box
                component="img"
                src="/images/food-tour/hannah-walk.png"
                alt="The Food Tour characters, out for a walk"
                sx={{ display: "block", width: "100%", height: "auto", maxHeight: { xs: 200, md: 280 }, objectFit: "contain", objectPosition: "center bottom" }}
              />
            </Box>
          </Box>
        </Container>
        <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={publicPageContainerSx}>
          <Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, minmax(0, 1fr))" }, gap: { xs: 1.5, md: 1.25 }, alignItems: "start" }}>
              {venueGroups.map((group) => (
                <ShowCard
                  key={group[0].id}
                  shows={group}
                  quantityFor={quantityFor}
                  pendingShowId={pendingShowId}
                  onChange={onChange}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
      <Box
        aria-live="polite"
        sx={{
          display: "block",
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 30,
          bgcolor: "#111",
          borderTop: `1px solid ${hairline}`,
          px: 2,
          pt: 1.25,
          pb: "calc(12px + env(safe-area-inset-bottom))",
          fontFamily: pageFont,
          color: textPrimary,
        }}
      >
        {lines.length > 0 && (
          <Box sx={{ maxHeight: 72, overflow: "auto", mb: 1 }}>
            <SummaryLines compact shows={shows} lines={lines} pendingShowId={pendingShowId} quantityFor={quantityFor} maxFor={maxFor} onChange={onChange} />
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ minWidth: 88 }}>
            <Box sx={{ fontSize: 11, letterSpacing: 0.8, color: textMuted, fontWeight: 800 }}>YOUR NIGHTS</Box>
            <Box sx={{ fontWeight: 800, fontSize: 20 }}>${Math.round(passTotal)}</Box>
          </Box>
          <Box sx={{ flex: 1 }}>{checkout}</Box>
        </Box>
      </Box>
      <Box sx={{ pb: { xs: "96px", md: "88px" } }}>
        <Footer />
      </Box>
    </>
  );
}
